import { ActionIcon, Loader, Tooltip } from '@mantine/core'
import { IconInfoCircle, IconMailShare } from '@tabler/icons-react'
import { useState } from 'react'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import {
  capitalizeFirstLetter,
  getActivityTooltip,
  STATUS_CONFIG,
  type UserStatus,
} from './service'

type Props = {
  patientId: string
  status: UserStatus
  lastLogin?: string | null
  lastInvitationSent?: string | null
  withResendInvitationButton?: boolean
}

/**
 * Presentational portal-status badge for the patient list. Unlike `PatientActivityStatus`, it renders
 * entirely from fields returned by /v2/patients (status + activity timestamps) with no per-row fetch.
 * Resend lazily reads the FHIR Patient only on click, so the common render path stays request-free.
 */
export function PatientStatusBadge({
  patientId,
  status,
  lastLogin,
  lastInvitationSent,
  withResendInvitationButton = false,
}: Props) {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const [isResending, setIsResending] = useState(false)
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.invited

  const handleResendInvitation = async () => {
    setIsResending(true)
    try {
      const patient = await medplum.readResource('Patient', patientId)
      await middleware.post('/patient/invite', { resource: patient })
      showNotification({ color: 'green', message: 'Invitation resent successfully' })
    } catch (error) {
      logError('patient', 'Resend invitation failed', error)
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Resend invitation failed',
      })
    } finally {
      setIsResending(false)
    }
  }

  const tone =
    config.color === 'green'
      ? 'bg-[#E6F6EC] text-[#1A8245]'
      : config.color === 'yellow'
        ? 'bg-[#FEF1E1] text-[#C2660A]'
        : 'bg-[#F1F3F5] text-[#697386]'

  return (
    <div className="flex items-center gap-1">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-semibold ${tone}`}
      >
        {capitalizeFirstLetter(config.label)}
        <Tooltip
          label={getActivityTooltip(config.label, { lastLogin, lastInvitationSent })}
          withArrow
        >
          <IconInfoCircle size={13} className="opacity-70" />
        </Tooltip>
      </span>
      {withResendInvitationButton && (
        <Tooltip
          label={
            config.label !== 'invited'
              ? 'Resend invitation with password reset link'
              : 'Resend invitation'
          }
          withArrow
        >
          <ActionIcon
            variant="transparent"
            color="gray"
            onClick={handleResendInvitation}
            disabled={isResending}
            size={20}
          >
            {isResending ? <Loader size={14} /> : <IconMailShare size={16} stroke={'1.5px'} />}
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  )
}
